/**
 * Shared presentational lead form. Replaces ~600 lines of duplicated JSX in
 * each per-route form component.
 *
 * Variants are config-driven — see `src/config/forms.ts` and `useLeadForm`.
 * Visual style matches the legacy slate-card + lime accent forms used by
 * /, /community, /ai-roadmap, /vk, /codex, /b2b, and vaibhav (NOT the new
 * /business page, which has its own component in `components/business/`).
 */

import type { ReactNode } from "react";
import { COUNTRIES } from "@/lib/countries";
import {
  useLeadForm,
  EXPERIENCE_OPTIONS,
  YEAR_OF_EXPERIENCE_OPTIONS,
  isOthersTitle,
} from "@/hooks/use-lead-form";
import type { Question } from "@/hooks/use-lead-form";
import type { FormConfig } from "@/config/forms";
import OptInCheckboxes from "@/components/forms/OptInCheckboxes";

const inputClasses =
  "w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-secondary/60 focus:outline-none focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 transition-[color,border-color,box-shadow]";

const selectClasses =
  "bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 transition-[color,border-color,box-shadow] appearance-none";

/** ISO 3166-1 alpha-2 → flag emoji via Regional Indicator Symbols. */
function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

function FieldError({ message, id }: { message?: string; id: string }) {
  if (!message) return null;
  return (
    <p
      id={id}
      role="alert"
      aria-live="polite"
      className="text-red-400 text-xs mt-1"
    >
      {message}
    </p>
  );
}

const validCheckmark = (
  <svg
    className="w-4 h-4 text-white absolute right-3 top-1/2 -translate-y-1/2"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

export type LeadFormProps = {
  config: FormConfig;
  /** Optional override for the submit button label. */
  submitLabel?: ReactNode;
  /** Optional PDF asset to deliver on success (used by /ai-roadmap). */
  pdfPath?: string;
};

export default function LeadForm({
  config,
  submitLabel,
  pdfPath,
}: LeadFormProps) {
  const f = useLeadForm({ config, pdfPath });

  const {
    geo,
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    dialCode,
    setDialCode,
    dialCountryCode,
    setDialCountryCode,
    country,
    countryCode,
    countryEditable,
    setCountryEditable,
    knowCoding,
    setKnowCoding,
    experience,
    setExperience,
    setExperienceOptionId,
    yearOfExperience,
    setYearOfExperience,
    enrollMastermind,
    setEnrollMastermind,
    subscribeNewsletter,
    setSubscribeNewsletter,
    designation,
    setDesignation,
    companyName,
    setCompanyName,
    teamSize,
    setTeamSize,
    programInterest,
    setProgramInterest,
    extraFieldQuestions,
    experienceOpen,
    setExperienceOpen,
    yearOfExperienceOpen,
    setYearOfExperienceOpen,
    dialCodeOpen,
    setDialCodeOpen,
    experienceRef,
    yearOfExperienceRef,
    dialCodeRef,
    dialCodeBtnRef,
    dialCodePos,
    setDialCodePos,
    loading,
    error,
    fieldErrors,
    successData,
    phoneMax,
    isFormComplete,
    professionalExperienceOptions,
    handleCountryChange,
    isFieldValid,
    handleSubmit,
    unmappedUnconditional,
    visibleFollowUps,
    dynamicAnswers,
    updateDynamicAnswer,
  } = f;

  /* ----------------------------------------------------------------------- */
  /*  Dynamic API question renderer                                           */
  /* ----------------------------------------------------------------------- */
  function renderApiQuestion(q: Question, animationDelay: string) {
    const id = q.formsQuestionIdentityId;
    const val = dynamicAnswers[id];
    const err = fieldErrors[`dyn-${id}`];
    const opts = q.options ?? [];
    const errId = `dyn-${id}-err`;

    function wrap(inner: ReactNode) {
      return (
        <div
          key={id}
          style={{ animationDelay }}
          className="animate-[fade-in_0.4s_ease-out_both] flex flex-col gap-1.5"
        >
          {inner}
        </div>
      );
    }

    if (q.questionCategory === "TEXT") {
      return wrap(
        <>
          <label htmlFor={`dyn-field-${id}`} className="text-sm text-white/90">
            {q.title}
            {q.isRequired ? <span className="text-red-400"> *</span> : null}
          </label>
          <input
            id={`dyn-field-${id}`}
            type="text"
            placeholder={q.placeholderText ?? undefined}
            value={val?.type === "text" ? val.value : ""}
            onChange={(e) =>
              updateDynamicAnswer(id, { type: "text", value: e.target.value })
            }
            className={`${inputClasses} ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
            aria-describedby={err ? errId : undefined}
          />
          <FieldError id={errId} message={err} />
        </>,
      );
    }

    if (q.questionCategory === "TEXT_AREA") {
      return wrap(
        <>
          <label htmlFor={`dyn-field-${id}`} className="text-sm text-white/90">
            {q.title}
            {q.isRequired ? <span className="text-red-400"> *</span> : null}
          </label>
          <textarea
            id={`dyn-field-${id}`}
            rows={4}
            placeholder={q.placeholderText ?? undefined}
            value={val?.type === "textarea" ? val.value : ""}
            onChange={(e) =>
              updateDynamicAnswer(id, {
                type: "textarea",
                value: e.target.value,
              })
            }
            className={`${inputClasses} min-h-[96px] resize-y ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
            aria-describedby={err ? errId : undefined}
          />
          <FieldError id={errId} message={err} />
        </>,
      );
    }

    if (q.questionCategory === "DROPDOWN") {
      const ch = val?.type === "choice" ? val : undefined;
      return wrap(
        <>
          <label htmlFor={`dyn-field-${id}`} className="text-sm text-white/90">
            {q.title}
            {q.isRequired ? <span className="text-red-400"> *</span> : null}
          </label>
          <select
            id={`dyn-field-${id}`}
            value={ch?.optionId ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (!raw) {
                updateDynamicAnswer(id, undefined);
                return;
              }
              const oid = Number(raw);
              const opt = opts.find((o) => o.optionId === oid);
              if (!opt) return;
              updateDynamicAnswer(id, {
                type: "choice",
                optionId: opt.optionId,
                title: opt.title,
              });
            }}
            className={`${selectClasses} w-full ${err ? "border-red-400/50" : ""}`}
            aria-invalid={!!err}
            aria-describedby={err ? errId : undefined}
          >
            <option value="">
              {q.isRequired ? "Select…" : "Optional — skip"}
            </option>
            {opts.map((o) => (
              <option key={o.optionId} value={o.optionId}>
                {o.title}
              </option>
            ))}
          </select>
          <FieldError id={errId} message={err} />
        </>,
      );
    }

    if (q.questionCategory === "ONE_OF_MANY") {
      const ch = val?.type === "choice" ? val : undefined;
      return wrap(
        <>
          <span id={`dyn-label-${id}`} className="text-sm text-white/90">
            {q.title}
            {q.isRequired ? <span className="text-red-400"> *</span> : null}
          </span>
          <div
            role="radiogroup"
            aria-labelledby={`dyn-label-${id}`}
            className="flex flex-col gap-2.5"
          >
            {opts.map((o) => (
              <label
                key={o.optionId}
                className="flex items-start gap-2.5 cursor-pointer"
              >
                <div className="relative w-4 h-4 mt-0.5 shrink-0">
                  <input
                    type="radio"
                    name={`dyn-radio-${id}`}
                    value={o.optionId}
                    checked={ch?.optionId === o.optionId}
                    onChange={() =>
                      updateDynamicAnswer(id, {
                        type: "choice",
                        optionId: o.optionId,
                        title: o.title,
                        otherText:
                          q.allowOthersOption && isOthersTitle(o.title)
                            ? (ch?.otherText ?? "")
                            : undefined,
                      })
                    }
                    className="w-4 h-4 cursor-pointer appearance-none transition-all absolute inset-0 rounded-full"
                    style={{
                      backgroundColor:
                        ch?.optionId === o.optionId ? "white" : "gray",
                      borderColor:
                        ch?.optionId === o.optionId
                          ? "white"
                          : "rgba(255,255,255,0.3)",
                    }}
                  />
                  {ch?.optionId === o.optionId && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-black rounded-full pointer-events-none" />
                  )}
                </div>
                <span className="text-sm text-white/90">{o.title}</span>
              </label>
            ))}
          </div>
          {ch && q.allowOthersOption && isOthersTitle(ch.title) && (
            <input
              type="text"
              placeholder="Please specify"
              value={ch.otherText ?? ""}
              onChange={(e) =>
                updateDynamicAnswer(id, {
                  type: "choice",
                  optionId: ch.optionId,
                  title: ch.title,
                  otherText: e.target.value,
                })
              }
              className={inputClasses}
            />
          )}
          <FieldError id={errId} message={err} />
        </>,
      );
    }

    return null;
  }

  /* ----------------------------------------------------------------------- */
  /*  Success state                                                          */
  /* ----------------------------------------------------------------------- */
  if (successData) {
    return (
      <section
        aria-live="polite"
        className="[overflow-anchor:none] scroll-mt-28 relative z-[1]"
      >
        <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center gap-4 min-h-[320px] w-full">
          <div className="relative flex items-center justify-center">
            <div
              className="absolute w-16 h-16 rounded-full bg-white/10 animate-[pulse-ring_2s_ease-out_infinite_0.5s]"
              aria-hidden="true"
            />
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center animate-[scale-in_0.4s_ease-out] relative">
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                  className="stroke-white animate-draw-check"
                  strokeDasharray={50}
                  strokeDashoffset={50}
                />
              </svg>
            </div>
          </div>

          <h2 className="font-fira-condensed text-2xl sm:text-3xl font-bold text-white">
            You&apos;re in!
          </h2>
          <p className="text-secondary text-base text-center max-w-sm">
            Hang tight — opening WhatsApp for you now.
          </p>

          <div
            className="flex flex-col items-center gap-3 pt-2 shrink-0"
            aria-live="polite"
            aria-label="Redirect in progress"
          >
            <svg
              className="animate-spin h-10 w-10 text-white shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <p className="text-secondary text-sm">Redirecting…</p>
          </div>
        </div>
      </section>
    );
  }

  /* ----------------------------------------------------------------------- */
  /*  Default submit label                                                   */
  /* ----------------------------------------------------------------------- */
  const defaultSubmitLabel = "Get Instant Access →";

  /* ----------------------------------------------------------------------- */
  /*  Form                                                                   */
  /* ----------------------------------------------------------------------- */
  return (
    <section className="[overflow-anchor:none]">
      {/* Mobile backdrop for any open dropdown */}
      {(dialCodeOpen || experienceOpen || yearOfExperienceOpen) && (
        <div
          className="fixed inset-0 bg-black/40 z-[65] sm:hidden"
          onClick={() => {
            setDialCodeOpen(false);
            setExperienceOpen(false);
            setYearOfExperienceOpen(false);
          }}
          aria-hidden="true"
        />
      )}

      {/* Dial code dropdown — rendered outside form to escape stacking contexts */}
      {dialCodeOpen && (
        <div
          ref={dialCodeRef}
          role="listbox"
          aria-label="Country calling code options"
          className="fixed z-[80] w-64 max-h-[min(15rem,40vh)] overflow-y-auto bg-[#1a1a1a] border border-white/10 rounded-lg py-1 shadow-[0_8px_30px_rgba(0,0,0,0.5)] dropdown-scroll [touch-action:pan-y]"
          style={{ top: dialCodePos.top, left: dialCodePos.left }}
        >
          {COUNTRIES.map((c) => (
            <button
              key={c.code}
              type="button"
              role="option"
              aria-selected={dialCountryCode === c.code}
              onClick={() => {
                setDialCode(c.dialCode);
                setDialCountryCode(c.code);
                setDialCodeOpen(false);
                const max = Array.isArray(c.phoneLength)
                  ? c.phoneLength[1]
                  : c.phoneLength;
                if (phone.length > max) setPhone(phone.slice(0, max));
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer hover:bg-white/10 hover:text-white ${
                dialCountryCode === c.code ? "text-white" : "text-white/80"
              }`}
            >
              <span className="mr-2">{countryFlag(c.code)}</span>
              {c.name} ({c.dialCode})
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        noValidate
        aria-busy={loading}
        className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-6 sm:p-8 flex flex-col gap-4"
      >
        <fieldset
          disabled={loading}
          className={`flex flex-col gap-4 ${loading ? "opacity-60" : "opacity-100"} transition-opacity duration-300`}
        >
          {/* Name */}
          <div
            style={{ animationDelay: "0ms" }}
            className="animate-[fade-in_0.4s_ease-out_both]"
          >
            <label htmlFor="name" className="sr-only">
              Your name
            </label>
            <div className="relative">
              <input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-describedby="name-error"
                aria-invalid={!!fieldErrors.name}
                className={`${inputClasses} ${fieldErrors.name ? "border-red-400/50" : ""} ${name.trim() && isFieldValid("name") ? "pr-10" : ""}`}
              />
              {name.trim() && isFieldValid("name") && validCheckmark}
            </div>
            <FieldError id="name-error" message={fieldErrors.name} />
          </div>

          {/* Email */}
          <div
            style={{ animationDelay: "60ms" }}
            className="animate-[fade-in_0.4s_ease-out_both]"
          >
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder={
                  config.extraFields ? "Work Email address" : "Email address"
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby="email-error"
                aria-invalid={!!fieldErrors.email}
                className={`${inputClasses} ${fieldErrors.email ? "border-red-400/50" : ""} ${email.trim() && isFieldValid("email") ? "pr-10" : ""}`}
              />
              {email.trim() && isFieldValid("email") && validCheckmark}
            </div>
            <FieldError id="email-error" message={fieldErrors.email} />
          </div>

          {/* Country */}
          <div
            style={{ animationDelay: "120ms" }}
            className="animate-[fade-in_0.4s_ease-out_both]"
          >
            <span id="country-label" className="sr-only">
              Country
            </span>
            {!geo.loaded ? (
              <div className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 h-12 flex items-center">
                <div className="h-4 w-full max-w-[160px] bg-gray-700 rounded animate-pulse" />
              </div>
            ) : countryEditable ? (
              <>
                <label htmlFor="country-select" className="sr-only">
                  Select country
                </label>
                <select
                  id="country-select"
                  value={countryCode}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  aria-labelledby="country-label"
                  className={`${selectClasses} w-full relative z-[1]`}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <div className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 h-12 flex items-center justify-between">
                <span className="text-white text-sm flex items-center gap-1.5">
                  <span role="img" aria-label="Location pin">
                    📍
                  </span>
                  {country} (detected)
                </span>
                <button
                  type="button"
                  onClick={() => setCountryEditable(true)}
                  className="text-white/70 text-xs hover:underline shrink-0 cursor-pointer"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          {/* Phone with dial code */}
          <div
            style={{ animationDelay: "180ms" }}
            className="animate-[fade-in_0.4s_ease-out_both]"
          >
            <div
              className={`flex items-center bg-[#1a1a1a] border rounded-lg transition-[color,border-color,box-shadow] ${
                fieldErrors.phone
                  ? "border-red-400/50"
                  : dialCodeOpen
                    ? "border-white/30"
                    : "border-white/10"
              } focus-within:border-white/40 focus-within:ring-1 focus-within:ring-white/40`}
            >
              <div className="relative shrink-0">
                <label htmlFor="dial-code-btn" className="sr-only">
                  Country calling code
                </label>
                <button
                  id="dial-code-btn"
                  ref={dialCodeBtnRef}
                  type="button"
                  onClick={() => {
                    if (!dialCodeOpen && dialCodeBtnRef.current) {
                      const rect =
                        dialCodeBtnRef.current.getBoundingClientRect();
                      setDialCodePos({
                        top: rect.bottom + 4,
                        left: rect.left,
                      });
                    }
                    setDialCodeOpen(!dialCodeOpen);
                  }}
                  aria-expanded={dialCodeOpen}
                  aria-haspopup="listbox"
                  className="flex items-center gap-1.5 pl-3 pr-2 py-3 cursor-pointer text-white bg-transparent border-none outline-none"
                >
                  <span className="text-base leading-none">
                    {countryFlag(dialCountryCode)}
                  </span>
                  <span className="text-sm">{dialCode}</span>
                  <svg
                    className={`w-3.5 h-3.5 text-secondary/60 transition-transform ${dialCodeOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
              </div>

              <div className="w-px h-5 bg-white/10 shrink-0" />

              <div className="flex-1 relative">
                <label htmlFor="phone" className="sr-only">
                  Phone number
                </label>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={phoneMax}
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "");
                    if (digits.length <= phoneMax) setPhone(digits);
                  }}
                  aria-describedby="phone-error"
                  aria-invalid={!!fieldErrors.phone}
                  className={`w-full bg-transparent border-none rounded-r-lg px-3 py-3 text-white placeholder:text-secondary/60 focus:outline-none ${
                    phone.trim() && isFieldValid("phone") ? "pr-10" : ""
                  }`}
                />
                {phone.trim() && isFieldValid("phone") && validCheckmark}
              </div>
            </div>
            <FieldError id="phone-error" message={fieldErrors.phone} />
          </div>

          {/* Know Coding (gated) */}
          {config.askKnowCoding && (
            <div
              className="animate-[fade-in_0.4s_ease-out_both] flex items-center gap-4"
              style={{ animationDelay: "320ms" }}
            >
              <label className="text-sm text-white/90 leading-snug block">
                Do you{" "}
                <span className="font-medium text-white">know Coding</span>?
              </label>
              <div className="flex items-center gap-6">
                {(["yes", "no"] as const).map((val) => (
                  <label
                    key={val}
                    className="flex items-center gap-2.5 cursor-pointer"
                  >
                    <div className="relative w-4 h-4">
                      <input
                        type="radio"
                        name="knowCoding"
                        value={val}
                        checked={knowCoding === val}
                        onChange={(e) =>
                          setKnowCoding(e.target.value as "yes" | "no")
                        }
                        className="w-4 h-4 border-white/30 rounded-full cursor-pointer appearance-none transition-all absolute inset-0"
                        style={{
                          backgroundColor:
                            knowCoding === val ? "white" : "gray",
                          borderColor:
                            knowCoding === val
                              ? "white"
                              : "rgba(255, 255, 255, 0.3)",
                        }}
                      />
                      {knowCoding === val && (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-black rounded-full pointer-events-none" />
                      )}
                    </div>
                    <span className="text-sm text-white/90 capitalize">
                      {val}
                    </span>
                  </label>
                ))}
              </div>
              <FieldError
                id="knowCoding-error"
                message={fieldErrors.knowCoding}
              />
            </div>
          )}

          {/* Experience (gated) */}
          {config.askExperience && (
            <div
              ref={experienceRef}
              className={`relative animate-[fade-in_0.4s_ease-out_both] ${experienceOpen ? "z-[70]" : ""}`}
              style={{ animationDelay: "240ms" }}
            >
              <label htmlFor="experience-btn" className="sr-only">
                What describes you best
              </label>
              <button
                id="experience-btn"
                type="button"
                onClick={() => setExperienceOpen(!experienceOpen)}
                aria-expanded={experienceOpen}
                aria-haspopup="listbox"
                aria-describedby="experience-error"
                aria-invalid={!!fieldErrors.experience}
                className={`${selectClasses} w-full text-left flex items-center justify-between cursor-pointer ${
                  !experience ? "text-secondary/60" : ""
                } ${fieldErrors.experience ? "border-red-400/50" : ""} ${experienceOpen ? "border-white/30" : ""}`}
              >
                <span className="flex items-center gap-2">
                  {experience || "What describes you the best"}
                  {experience && isFieldValid("experience") && (
                    <svg
                      className="w-4 h-4 text-white shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <svg
                  className={`w-4 h-4 text-secondary transition-transform ${experienceOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {experienceOpen && (
                <div
                  role="listbox"
                  aria-label="What describes you the best - options"
                  className="absolute z-[70] left-0 right-0 mt-1 bg-[#1a1a1a] border border-white/10 rounded-lg py-1 shadow-[0_8px_30px_rgba(0,0,0,0.5)] dropdown-scroll [touch-action:pan-y]"
                >
                  {(
                    professionalExperienceOptions ??
                    EXPERIENCE_OPTIONS.map((t) => ({ optionId: -1, title: t }))
                  ).map((opt) => (
                    <button
                      key={opt.optionId >= 0 ? opt.optionId : opt.title}
                      type="button"
                      role="option"
                      aria-selected={experience === opt.title}
                      onClick={() => {
                        setExperience(opt.title);
                        setExperienceOptionId(
                          opt.optionId >= 0 ? opt.optionId : null,
                        );
                        if (opt.title === "Student") {
                          setYearOfExperience("0 years (Fresher)");
                          setYearOfExperienceOpen(false);
                        } else if (experience === "Student") {
                          setYearOfExperience("");
                        }
                        setExperienceOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer hover:bg-white/10 hover:text-white ${
                        experience === opt.title
                          ? "text-white"
                          : "text-white/80"
                      }`}
                    >
                      {opt.title}
                    </button>
                  ))}
                </div>
              )}
              <FieldError
                id="experience-error"
                message={fieldErrors.experience}
              />
            </div>
          )}

          {/* Year of Experience (gated) */}
          {config.askYearOfExperience && experience !== "Student" && (
            <div
              ref={yearOfExperienceRef}
              className={`relative animate-[fade-in_0.4s_ease-out_both] ${yearOfExperienceOpen ? "z-[70]" : ""}`}
              style={{ animationDelay: "300ms" }}
            >
              <label htmlFor="year-of-experience-btn" className="sr-only">
                Years of experience
              </label>
              <button
                id="year-of-experience-btn"
                type="button"
                onClick={() => setYearOfExperienceOpen(!yearOfExperienceOpen)}
                aria-expanded={yearOfExperienceOpen}
                aria-haspopup="listbox"
                aria-describedby="year-of-experience-error"
                aria-invalid={!!fieldErrors.yearOfExperience}
                className={`${selectClasses} w-full text-left flex items-center justify-between cursor-pointer ${
                  !yearOfExperience ? "text-secondary/60" : ""
                } ${fieldErrors.yearOfExperience ? "border-red-400/50" : ""} ${yearOfExperienceOpen ? "border-white/30" : ""}`}
              >
                <span className="flex items-center gap-2">
                  {yearOfExperience ||
                    "How many years of work experience do you have?"}
                  {yearOfExperience && isFieldValid("yearOfExperience") && (
                    <svg
                      className="w-4 h-4 text-white shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <svg
                  className={`w-4 h-4 text-secondary transition-transform ${yearOfExperienceOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {yearOfExperienceOpen && (
                <div
                  role="listbox"
                  aria-label="Years of experience options"
                  className="absolute z-[70] left-0 right-0 mt-1 bg-[#1a1a1a] border border-white/10 rounded-lg py-1 shadow-[0_8px_30px_rgba(0,0,0,0.5)] dropdown-scroll [touch-action:pan-y]"
                >
                  {YEAR_OF_EXPERIENCE_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      role="option"
                      aria-selected={yearOfExperience === opt}
                      onClick={() => {
                        setYearOfExperience(opt);
                        setYearOfExperienceOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer hover:bg-white/10 hover:text-white ${
                        yearOfExperience === opt
                          ? "text-white"
                          : "text-white/80"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              <FieldError
                id="year-of-experience-error"
                message={fieldErrors.yearOfExperience}
              />
            </div>
          )}

          {/* Extra fields (b2b-only today) */}
          {config.extraFields?.includes("designation") && (
            <div className="animate-[fade-in_0.4s_ease-out_both]">
              <label htmlFor="designation" className="sr-only">
                Designation
              </label>
              <input
                id="designation"
                type="text"
                placeholder="Your Designation"
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                className={inputClasses}
              />
            </div>
          )}

          {config.extraFields?.includes("companyName") && (
            <div className="animate-[fade-in_0.4s_ease-out_both]">
              <label htmlFor="companyName" className="sr-only">
                Company Name
              </label>
              <input
                id="companyName"
                type="text"
                placeholder="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                aria-invalid={!!fieldErrors.companyName}
                className={`${inputClasses} ${fieldErrors.companyName ? "border-red-400/50" : ""}`}
              />
              <FieldError
                id="companyName-error"
                message={fieldErrors.companyName}
              />
            </div>
          )}

          {config.extraFields?.includes("teamSize") &&
            extraFieldQuestions.teamSize && (
              <div className="animate-[fade-in_0.4s_ease-out_both]">
                <label htmlFor="teamSize" className="sr-only">
                  Team Size
                </label>
                <select
                  id="teamSize"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  aria-invalid={!!fieldErrors.teamSize}
                  className={`${selectClasses} w-full ${fieldErrors.teamSize ? "border-red-400/50" : ""}`}
                >
                  <option value="">Team Size</option>
                  {extraFieldQuestions.teamSize.options?.map((opt) => (
                    <option key={opt.optionId} value={opt.optionId}>
                      {opt.title}
                    </option>
                  ))}
                </select>
                <FieldError
                  id="teamSize-error"
                  message={fieldErrors.teamSize}
                />
              </div>
            )}

          {config.extraFields?.includes("programInterest") &&
            extraFieldQuestions.programInterest && (
              <div className="animate-[fade-in_0.4s_ease-out_both]">
                <label htmlFor="programInterest" className="sr-only">
                  Program(s) you&apos;re looking for
                </label>
                <select
                  id="programInterest"
                  value={programInterest}
                  onChange={(e) => setProgramInterest(e.target.value)}
                  aria-invalid={!!fieldErrors.programInterest}
                  className={`${selectClasses} w-full ${fieldErrors.programInterest ? "border-red-400/50" : ""}`}
                >
                  <option value="">Program(s) you&apos;re looking for</option>
                  {extraFieldQuestions.programInterest.options?.map((opt) => (
                    <option key={opt.optionId} value={opt.optionId}>
                      {opt.title}
                    </option>
                  ))}
                </select>
                <FieldError
                  id="programInterest-error"
                  message={fieldErrors.programInterest}
                />
              </div>
            )}

          {/* Unconditional API questions (e.g. Team Location, "Where did you hear") */}
          {config.showApiExtraQuestions !== false && unmappedUnconditional.length > 0 && (
            <div className="flex flex-col gap-4 pt-2 border-t border-white/10">
              {unmappedUnconditional.map((q, i) =>
                renderApiQuestion(q, `${60 + i * 30}ms`),
              )}
            </div>
          )}

          {/* Follow-up questions (e.g. "What is your Goal") */}
          {config.showApiExtraQuestions !== false && visibleFollowUps.length > 0 && (
            <div className="flex flex-col gap-4 pt-2 border-t border-white/10">
              <p className="text-sm font-medium text-white">
                A few more questions
              </p>
              {visibleFollowUps.map((q, i) =>
                renderApiQuestion(q, `${120 + i * 30}ms`),
              )}
            </div>
          )}

          <div
            className="animate-[fade-in_0.4s_ease-out_both]"
            style={{ animationDelay: "300ms" }}
          >
            <OptInCheckboxes
              enrollMastermind={enrollMastermind}
              subscribeNewsletter={subscribeNewsletter}
              setEnrollMastermind={setEnrollMastermind}
              setSubscribeNewsletter={setSubscribeNewsletter}
            />
          </div>
        </fieldset>

        {/* Bottom section */}
        <div
          className="flex flex-col gap-4 animate-[fade-in_0.4s_ease-out_both]"
          style={{ animationDelay: "360ms" }}
        >
          {/* Social proof */}
          <div className="flex items-center gap-2.5 justify-center">
            <div className="flex items-center">
              {[
                { src: "/avatar-1.jpg", alt: "Community member" },
                { src: "/avatar-2.jpg", alt: "Community member" },
                { src: "/avatar-3.jpg", alt: "Community member" },
                { src: "/avatar-4.jpg", alt: "Community member" },
              ].map((avatar, i) => (
                <div
                  key={avatar.src}
                  className={`w-8 h-8 rounded-full border-2 border-[#1a1a1a] overflow-hidden shrink-0 ${i > 0 ? "-ml-2.5" : ""}`}
                >
                  <img
                    src={avatar.src}
                    alt={avatar.alt}
                    width={32}
                    height={32}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <span className="text-secondary text-xs">100,000+ joined</span>
          </div>

          {/* Error */}
          {error && (
            <div
              role="alert"
              aria-live="polite"
              className="bg-red-500/10 border border-red-400/20 rounded-lg px-4 py-3 text-red-400 text-sm text-center"
            >
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !isFormComplete}
            className="bg-white text-black font-semibold rounded-full w-full py-3.5 text-lg transition-all hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Joining...
              </>
            ) : (
              (submitLabel ?? defaultSubmitLabel)
            )}
          </button>

          <p className="text-secondary text-xs text-center flex items-center justify-center gap-1.5">
            <svg
              className="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            Your data is safe. No spam, ever.
          </p>
        </div>
      </form>
    </section>
  );
}
